import {
  type AnyRecord,
  RECORD_LIMITS,
  type RecordApiKind,
  type RecordListQuery,
  recordCreateSchemas,
} from '@fruuit/shared'
import {
  Callout,
  Checkbox,
  cn,
  DateInput,
  Input,
  Select,
  type SelectOption,
  Textarea,
} from '@fruuit/ui'
import type { ReactNode } from 'react'
import { ApiError, errorMessage } from '../../api/client'
import { useClients, useStaff } from './api'
import { RecordMultiPicker, RecordPicker } from './RecordPicker'

/**
 * Schema-driven record forms (FORM-001..006, P-UI-008). A dialog describes its fields as
 * `FieldSpec`s; limits come from `RECORD_LIMITS` and the payload is checked with the shared
 * zod schema before it is sent, so the client and the server never drift.
 */

interface SpecBase {
  name: string
  label: string
  hint?: ReactNode
  required?: boolean
  /** Span both columns of the form grid. */
  full?: boolean
}

export type FieldSpec = SpecBase &
  (
    | { type: 'text' | 'url' }
    | { type: 'textarea'; rows?: number }
    | { type: 'date' }
    | { type: 'select'; options: SelectOption[]; placeholder?: string }
    | { type: 'checkbox' }
    | { type: 'ref'; refKind: RecordApiKind; filter?: RefFilter; placeholder?: string }
    | { type: 'refs'; refKind: RecordApiKind; filter?: RefFilter }
    | { type: 'client' }
    | { type: 'staff'; role?: 'manager' | 'reviewer' }
    | { type: 'lines' }
  )

export type FormValues = Record<string, unknown>

/** Picker filters, optionally derived from the other values (e.g. the chosen issuer). */
export type RefFilter = RecordListQuery | ((values: FormValues) => RecordListQuery)

function resolveFilter(filter: RefFilter | undefined, values: FormValues) {
  return typeof filter === 'function' ? filter(values) : filter
}
export type FieldErrors = Record<string, string>

function limitOf(kind: RecordApiKind, name: string): { min: number; max: number } | undefined {
  return (RECORD_LIMITS[kind] as Record<string, { min: number; max: number }>)[name]
}

/** Initial form values for a spec list, from an optional record and defaults. */
export function initialValues(
  fields: readonly FieldSpec[],
  record?: Record<string, unknown> | null,
  defaults: FormValues = {},
): FormValues {
  const values: FormValues = {}
  for (const field of fields) {
    const fromRecord = record ? record[field.name] : undefined
    const fallback = defaults[field.name]
    const value = fromRecord !== undefined ? fromRecord : fallback
    if (field.type === 'refs') values[field.name] = Array.isArray(value) ? value : []
    else if (field.type === 'checkbox') values[field.name] = Boolean(value)
    else if (field.type === 'lines')
      values[field.name] = Array.isArray(value) ? value.join('\n') : (value ?? '')
    else values[field.name] = value ?? ''
  }
  return values
}

function toPayloadValue(field: FieldSpec, value: unknown): unknown {
  if (field.type === 'lines') {
    return String(value ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
  }
  return value
}

/**
 * Create payload (FORM-006): empty optional fields are omitted, references and lists sent as
 * arrays, booleans as booleans.
 */
export function createPayload(fields: readonly FieldSpec[], values: FormValues): FormValues {
  const payload: FormValues = {}
  for (const field of fields) {
    const value = toPayloadValue(field, values[field.name])
    if (value === '' || value === null || value === undefined) continue
    if (Array.isArray(value) && value.length === 0 && field.type === 'lines') continue
    payload[field.name] = value
  }
  return payload
}

/** Update payload: only changed fields; a cleared optional field is sent as null. */
export function updatePayload(
  fields: readonly FieldSpec[],
  values: FormValues,
  initial: FormValues,
): FormValues {
  const payload: FormValues = {}
  for (const field of fields) {
    const next = toPayloadValue(field, values[field.name])
    const before = toPayloadValue(field, initial[field.name])
    if (JSON.stringify(next) === JSON.stringify(before)) continue
    payload[field.name] = next === '' ? null : next
  }
  return payload
}

/** zod issues -> `{ field: message }` (first message per field). */
export function issuesToErrors(
  issues: readonly { path: readonly PropertyKey[]; message: string }[],
): {
  fields: FieldErrors
  form: string | null
} {
  const fields: FieldErrors = {}
  let form: string | null = null
  for (const issue of issues) {
    const key = issue.path.map(String).join('.')
    if (!key) {
      form ??= issue.message
      continue
    }
    const top = key.split('.')[0] ?? key
    if (!(top in fields)) fields[top] = issue.message
  }
  return { fields, form }
}

/**
 * Validate a create payload with the shared schema. `only` limits the reported fields to
 * those on the form (edits validate the merged record but show the form's fields).
 */
export function validateRecord(
  kind: RecordApiKind,
  payload: FormValues,
  only?: readonly string[],
): { fields: FieldErrors; form: string | null } {
  const result = recordCreateSchemas[kind].safeParse(payload)
  if (result.success) return { fields: {}, form: null }
  const issues = only
    ? result.error.issues.filter((issue) => {
        const top = String(issue.path[0] ?? '')
        return top !== '' && only.includes(top)
      })
    : result.error.issues
  return issuesToErrors(issues)
}

/** Field errors and a form-level message from a failed request (FORM-024, FORM-027). */
export function serverErrors(error: unknown): { fields: FieldErrors; form: string } {
  if (error instanceof ApiError) {
    const fields: FieldErrors = {}
    for (const [key, message] of Object.entries(error.fieldErrors)) {
      fields[key.split('.')[0] ?? key] = message
    }
    if (error.code === 'stale_revision') {
      return {
        fields,
        form: 'This record changed while you were editing. Your input is kept: reload the latest version, then reapply your changes.',
      }
    }
    return { fields, form: error.message }
  }
  return { fields: {}, form: errorMessage(error) }
}

function ClientSelect({
  spec,
  value,
  error,
  onChange,
  disabled,
}: {
  spec: FieldSpec
  value: string
  error: string | undefined
  onChange: (value: string) => void
  disabled: boolean | undefined
}) {
  const { data } = useClients()
  return (
    <Select
      label={spec.label}
      hint={spec.hint ?? 'Shared content is visible to every subscribing client (D-03).'}
      error={error}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      options={[
        { value: '', label: 'Shared with all subscribers' },
        ...(data?.items ?? []).map((client) => ({ value: client.id, label: client.name })),
      ]}
    />
  )
}

function StaffSelect({
  spec,
  value,
  error,
  onChange,
  disabled,
}: {
  spec: FieldSpec & { type: 'staff' }
  value: string
  error: string | undefined
  onChange: (value: string) => void
  disabled: boolean | undefined
}) {
  const { data } = useStaff()
  const people = (data?.items ?? []).filter(
    (person) => !spec.role || person.roles.includes(spec.role),
  )
  return (
    <Select
      label={spec.label}
      hint={spec.hint}
      error={error}
      required={spec.required}
      value={value}
      disabled={disabled}
      placeholder={spec.role === 'reviewer' ? 'Choose a reviewer' : 'Choose a Fruuit colleague'}
      onChange={(event) => onChange(event.target.value)}
      options={people.map((person) => ({ value: person.id, label: person.name }))}
    />
  )
}

export interface RecordFieldsProps {
  kind: RecordApiKind
  fields: readonly FieldSpec[]
  values: FormValues
  errors: FieldErrors
  onChange: (name: string, value: unknown) => void
  disabled?: boolean
  /** Field names that cannot change (shown read-only on edit). */
  locked?: readonly string[]
  className?: string
}

/** Renders a spec list in a responsive two-column grid. */
export function RecordFields({
  kind,
  fields,
  values,
  errors,
  onChange,
  disabled,
  locked = [],
  className,
}: RecordFieldsProps) {
  return (
    <div className={cn('grid gap-x-5 gap-y-4 sm:grid-cols-2', className)}>
      {fields.map((field) => {
        const limit = limitOf(kind, field.name)
        const error = errors[field.name]
        const isLocked = disabled || locked.includes(field.name)
        const span = field.full || field.type === 'textarea' || field.type === 'refs'
        const common = {
          label: field.label,
          hint: field.hint,
          error,
          required: field.required,
          disabled: isLocked,
        }
        const value = values[field.name]
        const text = typeof value === 'string' ? value : ''
        let control: ReactNode
        switch (field.type) {
          case 'text':
          case 'url':
            control = (
              <Input
                {...common}
                type={field.type === 'url' ? 'url' : 'text'}
                value={text}
                maxLength={limit?.max}
                onChange={(event) => onChange(field.name, event.target.value)}
              />
            )
            break
          case 'textarea':
            control = (
              <Textarea
                {...common}
                rows={field.rows ?? 3}
                value={text}
                maxLength={limit?.max}
                onChange={(event) => onChange(field.name, event.target.value)}
              />
            )
            break
          case 'lines':
            control = (
              <Textarea
                {...common}
                rows={2}
                value={text}
                onChange={(event) => onChange(field.name, event.target.value)}
              />
            )
            break
          case 'date':
            control = (
              <DateInput
                {...common}
                value={text}
                onChange={(event) => onChange(field.name, event.target.value)}
              />
            )
            break
          case 'select':
            control = (
              <Select
                {...common}
                value={text}
                options={field.options}
                placeholder={field.placeholder ?? (field.required ? 'Choose…' : 'Not recorded')}
                onChange={(event) => onChange(field.name, event.target.value)}
              />
            )
            break
          case 'checkbox':
            control = (
              <Checkbox
                label={field.label}
                hint={field.hint}
                error={error}
                disabled={isLocked}
                checked={Boolean(value)}
                onChange={(event) => onChange(field.name, event.target.checked)}
              />
            )
            break
          case 'ref':
            control = (
              <RecordPicker
                {...common}
                kind={field.refKind}
                filter={resolveFilter(field.filter, values)}
                placeholder={field.placeholder}
                value={text || null}
                onChange={(id) => onChange(field.name, id ?? '')}
              />
            )
            break
          case 'refs':
            control = (
              <RecordMultiPicker
                {...common}
                kind={field.refKind}
                filter={resolveFilter(field.filter, values)}
                value={Array.isArray(value) ? (value as string[]) : []}
                onChange={(ids) => onChange(field.name, ids)}
              />
            )
            break
          case 'client':
            control = (
              <ClientSelect
                spec={field}
                value={text}
                error={error}
                disabled={isLocked}
                onChange={(next) => onChange(field.name, next)}
              />
            )
            break
          case 'staff':
            control = (
              <StaffSelect
                spec={field}
                value={text}
                error={error}
                disabled={isLocked}
                onChange={(next) => onChange(field.name, next)}
              />
            )
            break
        }
        return (
          <div key={field.name} className={cn('min-w-0', span && 'sm:col-span-2')}>
            {control}
          </div>
        )
      })}
    </div>
  )
}

/** Inline form-level error (FORM-024: shown in the dialog, which stays open). */
export function FormAlert({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <Callout tone="danger" role="alert" className="mb-4">
      {message}
    </Callout>
  )
}

/** Narrow a mutation result to a record of a kind (for `onSaved` callbacks). */
export type SavedRecord = AnyRecord
